// Multi-Channel Communication Service
// Manages SMS, Bot Calls, and Email for appointment requests
// Ensures no appointment request is missed

import { smsService } from './smsService';
import { botCallService } from './botCallService';
import { appointmentRequestService, AppointmentRequest } from './appointmentRequestService';
import { phoneValidationService } from './phoneValidationService';
import { emailService } from './emailService';

export interface CommunicationChannel {
  type: 'sms' | 'call' | 'email';
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'responded';
  timestamp: string;
  error?: string;
}

export interface MultiChannelResult {
  success: boolean;
  channels: CommunicationChannel[];
  primaryChannel: 'sms' | 'call' | 'email';
  message?: string;
  error?: string;
}

class MultiChannelService {
  private readonly RESPONSE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
  private readonly ESCALATION_DELAY_MS = 15 * 60 * 1000; // 15 minutes before escalation
  private readonly ADMIN_ESCALATION_DELAY_MS = 2 * 60 * 60 * 1000; // 2 hours before admin escalation
  // TODO: TEMPORARY - Remove admin escalation SMS in future
  private readonly ADMIN_PHONE = '+12489075504'; // Admin phone for manual appointment setup
  private readonly ADMIN_ESCALATION_EMAIL = 'info@orynsolutions.io'; // Admin email for escalation alerts

  /**
   * Send appointment request via best available channel
   */
  async sendAppointmentRequest(
    request: AppointmentRequest
  ): Promise<MultiChannelResult> {
    const channels: CommunicationChannel[] = [];
    
    try {
      // Step 1: Validate phone number and determine best channel
      const validationResult = await phoneValidationService.validatePhoneNumber(request.businessPhone);
      
      let primaryChannel: 'sms' | 'call' | 'email' = 'sms';
      
      // Determine primary channel based on phone type
      if (validationResult.isSmsCapable) {
        primaryChannel = 'sms';
      } else if (validationResult.carrierType === 'landline') {
        primaryChannel = 'call'; // Use bot call for landlines
      } else {
        // Fallback to email if available, otherwise try call anyway
        primaryChannel = request.businessEmail ? 'email' : 'call';
      }

      // Step 2: Send via primary channel
      if (primaryChannel === 'sms') {
        const smsResult = await this.sendSMS(request);
        channels.push({
          type: 'sms',
          status: smsResult.success ? 'sent' : 'failed',
          timestamp: new Date().toISOString(),
          error: smsResult.error,
        });
        
        // If SMS fails immediately and email is available, send email as fallback
        if (!smsResult.success && request.businessEmail) {
          console.log('⚠️ SMS failed - immediately sending email fallback:', request.businessEmail);
          const emailResult = await this.sendEmail(request);
          channels.push({
            type: 'email',
            status: emailResult.success ? 'sent' : 'failed',
            timestamp: new Date().toISOString(),
            error: emailResult.error,
          });
        }
      } else if (primaryChannel === 'call') {
        const callResult = await botCallService.makeAppointmentCall(request, 'landline');
        channels.push({
          type: 'call',
          status: callResult.success ? 'sent' : 'failed',
          timestamp: new Date().toISOString(),
          error: callResult.error,
        });
      } else if (primaryChannel === 'email' && request.businessEmail) {
        // Send email as primary channel
        const emailResult = await this.sendEmail(request);
        channels.push({
          type: 'email',
          status: emailResult.success ? 'sent' : 'failed',
          timestamp: new Date().toISOString(),
          error: emailResult.error,
        });
      }

      // Step 3: Schedule escalation if no response
      this.scheduleEscalation(request.id, primaryChannel);
      
      // Step 4: Schedule admin escalation after 2 hours (TEMPORARY - will remove later)
      this.scheduleAdminEscalation(request.id);

      return {
        success: true,
        channels,
        primaryChannel,
        message: `Appointment request sent via ${primaryChannel.toUpperCase()}`,
      };
    } catch (error: any) {
      console.error('Error in multi-channel service:', error);
      return {
        success: false,
        channels,
        primaryChannel: 'sms',
        error: error.message,
      };
    }
  }

  /**
   * Send SMS
   */
  private async sendSMS(request: AppointmentRequest): Promise<{ success: boolean; error?: string }> {
    try {
      const message = this.generateSMSMessage(request);
      const success = await smsService.sendSMS(request.businessPhone, message);
      return {
        success,
        error: success ? undefined : 'Failed to send SMS',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Send Email appointment request
   */
  private async sendEmail(request: AppointmentRequest): Promise<{ success: boolean; error?: string }> {
    try {
      if (!request.businessEmail) {
        return {
          success: false,
          error: 'Business email not available',
        };
      }

      // Check if email service is initialized
      // If not, try to send via backend API
      const emailHtml = this.generateEmailMessage(request);
      const emailText = this.generateEmailTextMessage(request);

      // Try to send via backend API (preferred method)
      try {
        const backendUrl = this.getBackendUrl();
        
        const response = await fetch(`${backendUrl}/api/email/send-appointment-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: request.businessEmail,
            subject: `New Appointment Request - ${request.userName}`,
            html: emailHtml,
            text: emailText,
            appointmentRequest: {
              id: request.id,
              userName: request.userName,
              userPhone: request.userPhone,
              userEmail: request.userEmail,
              businessName: request.businessName,
              service: request.service,
              preferredDate: request.preferredDate,
              preferredTime: request.preferredTime,
              specialRequests: request.specialRequests,
              pickupAddress: request.pickupAddress,
              dropoffAddress: request.dropoffAddress,
            },
          }),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            console.log('✅ Appointment request email sent via backend:', request.businessEmail);
            return {
              success: true,
            };
          }
        }
      } catch (apiError) {
        console.warn('⚠️ Backend email API failed, trying direct email service:', apiError);
      }

      // Fallback: Try direct email service if initialized
      if (emailService && typeof (emailService as any).sendEmail === 'function') {
        const result = await (emailService as any).sendEmail({
          to: request.businessEmail,
          subject: `New Appointment Request - ${request.userName}`,
          html: emailHtml,
          text: emailText,
        });

        if (result.success) {
          console.log('✅ Appointment request email sent via email service:', request.businessEmail);
          return {
            success: true,
          };
        } else {
          return {
            success: false,
            error: result.error || 'Failed to send email',
          };
        }
      }

      // If both methods fail, return error
      return {
        success: false,
        error: 'Email service not available',
      };
    } catch (error: any) {
      console.error('❌ Error sending appointment request email:', error);
      return {
        success: false,
        error: error.message || 'Failed to send email',
      };
    }
  }

  /**
   * Schedule escalation if no response received
   */
  private scheduleEscalation(requestId: string, primaryChannel: 'sms' | 'call' | 'email'): void {
    // In production, use a job queue (e.g., Bull, Agenda.js)
    // For now, we'll use setTimeout (not ideal for production)
    
    setTimeout(async () => {
      try {
        const request = await appointmentRequestService.getRequest(requestId);
        if (!request || request.status !== 'pending') {
          return; // Already responded or doesn't exist
        }

        console.log(`⏰ Escalating request ${requestId} - no response after ${this.ESCALATION_DELAY_MS / 1000 / 60} minutes`);

        // Escalate to secondary channel
        if (primaryChannel === 'sms') {
          // If SMS sent but no response, try email first (if available), then bot call
          if (request.businessEmail) {
            console.log('📧 SMS no response - escalating to email:', request.businessEmail);
            await this.sendEmail(request);
          } else {
            // No email available, try bot call
            console.log('📞 SMS no response - escalating to bot call');
            await botCallService.makeAppointmentCall(request, 'no_sms_response');
          }
        } else if (primaryChannel === 'call') {
          // If call made but no response, try SMS (if mobile) or email
          const validationResult = await phoneValidationService.validatePhoneNumber(request.businessPhone);
          if (validationResult.isSmsCapable) {
            await this.sendSMS(request);
          } else if (request.businessEmail) {
            // Send email
            console.log('📧 Call no response - escalating to email:', request.businessEmail);
            await this.sendEmail(request);
          }
        }

        // Notify user of escalation and update status
        await appointmentRequestService.sendStatusUpdate(request, 'escalating');
      } catch (error) {
        console.error('Error in escalation:', error);
      }
    }, this.ESCALATION_DELAY_MS);
  }

  /**
   * Schedule admin escalation after 2 hours (TEMPORARY - will remove later)
   * Sends SMS to admin with customer details for manual appointment setup
   */
  private scheduleAdminEscalation(requestId: string): void {
    // TODO: TEMPORARY - Remove this function in future
    // This is an early-stage strategy to ensure no appointments are missed
    // Will be removed once automated system is fully operational
    
    setTimeout(async () => {
      try {
        const request = await appointmentRequestService.getRequest(requestId);
        if (!request || request.status !== 'pending') {
          return; // Already responded or doesn't exist
        }

        console.log(`🚨 Admin escalation triggered for request ${requestId} - no response after 2 hours`);

        // Generate admin notification message
        const adminMessage = this.generateAdminEscalationMessage(request);
        
        // Send SMS to admin
        const smsSuccess = await smsService.sendSMS(this.ADMIN_PHONE, adminMessage);
        
        if (smsSuccess) {
          console.log(`✅ Admin escalation SMS sent to ${this.ADMIN_PHONE} for request ${requestId}`);
        } else {
          console.error('❌ Failed to send admin escalation SMS');
        }

        // Also send escalation alert to admin email
        const emailResult = await this.sendAdminEscalationEmail(request);
        if (emailResult.success) {
          console.log(`✅ Admin escalation email sent to ${this.ADMIN_ESCALATION_EMAIL} for request ${requestId}`);
        } else {
          console.error(`❌ Failed to send admin escalation email: ${emailResult.error}`);
        }
      } catch (error) {
        console.error('Error in admin escalation:', error);
      }
    }, this.ADMIN_ESCALATION_DELAY_MS);
  }

  /**
   * Generate admin escalation SMS message
   * Includes customer details and business phone for manual setup
   */
  private generateAdminEscalationMessage(request: AppointmentRequest): string {
    const formattedDate = this.formatDate(request.preferredDate);
    
    let message = `🚨 ESCALATION: No Response from Business\n\n`;
    message += `Request ID: ${request.id}\n`;
    message += `Escalated after 2 hours of no response\n\n`;
    
    message += `📋 Customer Details:\n`;
    message += `Name: ${request.userName}\n`;
    message += `Phone: ${request.userPhone}\n`;
    message += `Email: ${request.userEmail || 'N/A'}\n\n`;
    
    message += `🏢 Business Details:\n`;
    message += `Name: ${request.businessName}\n`;
    message += `Phone: ${request.businessPhone}\n`;
    message += `Email: ${request.businessEmail || 'N/A'}\n\n`;
    
    message += `📅 Appointment Request:\n`;
    message += `Service: ${request.service}\n`;
    message += `Date: ${formattedDate}\n`;
    message += `Time: ${request.preferredTime}\n`;
    if (request.pickupAddress?.trim()) {
      message += `Pickup: ${request.pickupAddress.trim()}\n`;
    }
    if (request.dropoffAddress?.trim()) {
      message += `Drop-off: ${request.dropoffAddress.trim()}\n`;
    }
    
    if (request.specialRequests) {
      message += `Notes: ${request.specialRequests}\n`;
    }
    
    message += `\n⚠️ ACTION REQUIRED:\n`;
    message += `Please contact business at ${request.businessPhone} to set up appointment manually.\n`;
    message += `Customer contact: ${request.userPhone}`;

    return message;
  }

  /**
   * Send admin escalation email with customer + business details
   */
  private async sendAdminEscalationEmail(request: AppointmentRequest): Promise<{ success: boolean; error?: string }> {
    try {
      const formattedDate = this.formatDate(request.preferredDate);
      const subject = `Admin Escalation: No Business Response (${request.businessName})`;
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Admin Escalation</title>
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.5; color: #222;">
          <h2>Admin Escalation: No Response from Business</h2>
          <p><strong>Request ID:</strong> ${request.id}</p>
          <p><strong>Escalation Trigger:</strong> No response after 2 hours</p>
          <h3>Customer Details</h3>
          <p><strong>Name:</strong> ${request.userName}</p>
          <p><strong>Phone:</strong> ${request.userPhone}</p>
          <p><strong>Email:</strong> ${request.userEmail || 'N/A'}</p>
          <h3>Business Details</h3>
          <p><strong>Name:</strong> ${request.businessName}</p>
          <p><strong>Phone:</strong> ${request.businessPhone}</p>
          <p><strong>Email:</strong> ${request.businessEmail || 'N/A'}</p>
          <h3>Appointment Request</h3>
          <p><strong>Service:</strong> ${request.service}</p>
          <p><strong>Date:</strong> ${formattedDate}</p>
          <p><strong>Time:</strong> ${request.preferredTime}</p>
          ${request.pickupAddress?.trim() ? `<p><strong>Pickup:</strong> ${request.pickupAddress.trim()}</p>` : ''}
          ${request.dropoffAddress?.trim() ? `<p><strong>Drop-off:</strong> ${request.dropoffAddress.trim()}</p>` : ''}
          ${request.specialRequests ? `<p><strong>Notes:</strong> ${request.specialRequests}</p>` : ''}
          <hr />
          <p><strong>Action required:</strong> Contact business at ${request.businessPhone} to set up appointment manually.</p>
        </body>
        </html>
      `;
      const text = this.generateAdminEscalationMessage(request);

      const backendUrl = this.getBackendUrl();

      try {
        const response = await fetch(`${backendUrl}/api/email/send-appointment-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: this.ADMIN_ESCALATION_EMAIL,
            subject,
            html,
            text,
            appointmentRequest: {
              id: request.id,
              userName: request.userName,
              userPhone: request.userPhone,
              userEmail: request.userEmail,
              businessName: request.businessName,
              service: request.service,
              preferredDate: request.preferredDate,
              preferredTime: request.preferredTime,
              specialRequests: request.specialRequests,
              pickupAddress: request.pickupAddress,
              dropoffAddress: request.dropoffAddress,
            },
          }),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            return { success: true };
          }
        }
      } catch (apiError: any) {
        console.warn('⚠️ Backend admin escalation email API failed, trying direct email service:', apiError?.message || apiError);
      }

      if (emailService && typeof (emailService as any).sendEmail === 'function') {
        const result = await (emailService as any).sendEmail({
          to: this.ADMIN_ESCALATION_EMAIL,
          subject,
          html,
          text,
        });

        if (result.success) {
          return { success: true };
        }

        return {
          success: false,
          error: result.error || 'Failed to send admin escalation email',
        };
      }

      return {
        success: false,
        error: 'Email service not available',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error?.message || 'Failed to send admin escalation email',
      };
    }
  }

  /**
   * Generate SMS message
   */
  private generateSMSMessage(request: AppointmentRequest): string {
    const formattedDate = this.formatDate(request.preferredDate);
    
    let message = `Hi ${request.businessName}! 👋\n\n`;
    message += `You have a new appointment request from BookAPT:\n\n`;
    message += `Customer: ${request.userName}\n`;
    message += `Phone: ${request.userPhone}\n`;
    message += `Service: ${request.service}\n`;
    message += `Date: ${formattedDate}\n`;
    message += `Time: ${request.preferredTime}\n`;
    if (request.pickupAddress?.trim()) {
      message += `Pickup: ${request.pickupAddress.trim()}\n`;
    }
    if (request.dropoffAddress?.trim()) {
      message += `Drop-off: ${request.dropoffAddress.trim()}\n`;
    }
    
    if (request.specialRequests) {
      message += `Notes: ${request.specialRequests}\n`;
    }
    
    message += `\nReply options:\n`;
    message += `✅ YES - Confirm appointment\n`;
    message += `❌ NO - Decline\n`;
    message += `📞 CALL - Call customer to discuss\n`;
    message += `🔄 RESCHEDULE - Suggest different time\n\n`;
    message += `Powered by BookAPT`;

    return message;
  }

  /**
   * Generate email HTML message
   */
  private generateEmailMessage(request: AppointmentRequest): string {
    const formattedDate = this.formatDate(request.preferredDate);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New Appointment Request</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #007bff; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
          .content { padding: 20px; background: #f8f9fa; border: 1px solid #dee2e6; }
          .info-box { background: white; padding: 15px; margin: 15px 0; border-radius: 4px; border-left: 4px solid #007bff; }
          .info-label { font-weight: bold; color: #495057; }
          .info-value { color: #212529; margin-bottom: 10px; }
          .actions { background: white; padding: 20px; margin: 20px 0; border-radius: 4px; }
          .action-button { display: inline-block; padding: 12px 24px; margin: 5px; background: #007bff; color: white; text-decoration: none; border-radius: 4px; }
          .footer { text-align: center; padding: 20px; color: #6c757d; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>📅 New Appointment Request</h1>
            <p>From BookAPT</p>
          </div>
          <div class="content">
            <p>Hi ${request.businessName}!</p>
            <p>You have a new appointment request from a customer:</p>
            
            <div class="info-box">
              <div class="info-value"><span class="info-label">Customer Name:</span> ${request.userName}</div>
              <div class="info-value"><span class="info-label">Phone:</span> ${request.userPhone}</div>
              ${request.userEmail ? `<div class="info-value"><span class="info-label">Email:</span> ${request.userEmail}</div>` : ''}
              <div class="info-value"><span class="info-label">Service:</span> ${request.service}</div>
              <div class="info-value"><span class="info-label">Preferred Date:</span> ${formattedDate}</div>
              <div class="info-value"><span class="info-label">Preferred Time:</span> ${request.preferredTime}</div>
              ${request.pickupAddress?.trim() ? `<div class="info-value"><span class="info-label">Pickup:</span> ${request.pickupAddress.trim()}</div>` : ''}
              ${request.dropoffAddress?.trim() ? `<div class="info-value"><span class="info-label">Drop-off:</span> ${request.dropoffAddress.trim()}</div>` : ''}
              ${request.specialRequests ? `<div class="info-value"><span class="info-label">Special Requests:</span> ${request.specialRequests}</div>` : ''}
            </div>
            
            <div class="actions">
              <p><strong>How to respond:</strong></p>
              <p>Reply to this email with one of the following:</p>
              <ul>
                <li><strong>YES</strong> or <strong>CONFIRM</strong> - Confirm the appointment</li>
                <li><strong>NO</strong> or <strong>DECLINE</strong> - Decline the appointment</li>
                <li><strong>CALL</strong> - Request to call the customer</li>
                <li><strong>RESCHEDULE</strong> - Suggest a different time</li>
              </ul>
              <p>Or reply via SMS to: ${request.userPhone}</p>
            </div>
            
            <p>Thank you for using BookAPT!</p>
          </div>
          <div class="footer">
            <p>This is an automated message from BookAPT</p>
            <p>Request ID: ${request.id}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generate email plain text message
   */
  private generateEmailTextMessage(request: AppointmentRequest): string {
    const formattedDate = this.formatDate(request.preferredDate);
    
    let text = `New Appointment Request from BookAPT\n\n`;
    text += `Hi ${request.businessName}!\n\n`;
    text += `You have a new appointment request:\n\n`;
    text += `Customer Name: ${request.userName}\n`;
    text += `Phone: ${request.userPhone}\n`;
    if (request.userEmail) {
      text += `Email: ${request.userEmail}\n`;
    }
    text += `Service: ${request.service}\n`;
    text += `Preferred Date: ${formattedDate}\n`;
    text += `Preferred Time: ${request.preferredTime}\n`;
    if (request.pickupAddress?.trim()) {
      text += `Pickup: ${request.pickupAddress.trim()}\n`;
    }
    if (request.dropoffAddress?.trim()) {
      text += `Drop-off: ${request.dropoffAddress.trim()}\n`;
    }
    if (request.specialRequests) {
      text += `Special Requests: ${request.specialRequests}\n`;
    }
    text += `\nHow to respond:\n`;
    text += `Reply to this email with: YES, NO, CALL, or RESCHEDULE\n`;
    text += `Or reply via SMS to: ${request.userPhone}\n\n`;
    text += `Thank you for using BookAPT!\n`;
    text += `Request ID: ${request.id}\n`;
    
    return text;
  }

  /**
   * Format date
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Resolve backend URL from runtime env with safe fallback.
   */
  private getBackendUrl(): string {
    const env = (globalThis as any)?.process?.env || {};
    return env.REACT_APP_BACKEND_URL ||
      env.REACT_APP_API_URL ||
      'https://bookapt-payment-api.onrender.com';
  }
}

export const multiChannelService = new MultiChannelService();
export default multiChannelService;

